import React from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/common/Sidebar";
import { registerVSUser } from "../services/vsUserService";
import { clearClientSession, markClientRegistered } from "../services/clientAuth";
import "./Register.css";

export default function Register() {
  const navigate = useNavigate();
  const emptyFormState = {
    name: "",
    phone: "",
    email: "",
    password: "",
    address: "",
    securityAnswer: "",
  };
  const [formData, setFormData] = React.useState({
    ...emptyFormState,
  });
  const [errorMessage, setErrorMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [securityPrompt, setSecurityPrompt] = React.useState({ left: 0, right: 0 });

  const regenerateSecurityPrompt = React.useCallback(() => {
    const left = Math.floor(Math.random() * 90) + 10;
    const right = Math.floor(Math.random() * 9) + 1;
    setSecurityPrompt({ left, right });
  }, []);

  React.useEffect(() => {
    regenerateSecurityPrompt();
  }, [regenerateSecurityPrompt]);

  // Force clear autofill on mount
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFormData({ ...emptyFormState });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    
    if (name === "phone") {
      // Only allow numeric digits and limit to 10
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFormFields = () => {
    setFormData(emptyFormState);
  };

  const handleReset = () => {
    resetFormFields();
    setErrorMessage("");
    setSuccessMessage("");
    regenerateSecurityPrompt();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (
      !formData.name ||
      !formData.phone ||
      !formData.email ||
      !formData.password ||
      !formData.address
    ) {
      setErrorMessage("Please fill all required fields.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone.trim())) {
      setErrorMessage("Please enter a valid 10-digit phone number.");
      return;
    }

    if (String(securityPrompt.left + securityPrompt.right) !== formData.securityAnswer.trim()) {
      setErrorMessage("Security code answer is incorrect.");
      regenerateSecurityPrompt();
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await registerVSUser({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        password: formData.password,
        address: formData.address,
      });
      markClientRegistered(response?.data?.email || formData.email);
      clearClientSession();
      setSuccessMessage("Registration successful.");
      resetFormFields();
      regenerateSecurityPrompt();
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Registration failed. Please try again."
      );
      regenerateSecurityPrompt();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content-area">
        <section className="register-panel">
          <div className="register-header-container">
            <div className="title-white">Registration</div>
          </div>

          <form className="register-form" onSubmit={handleSubmit} autoComplete="off">
            {errorMessage ? <p className="register-feedback register-feedback--error">{errorMessage}</p> : null}
            {successMessage ? (
              <p className="register-feedback register-feedback--success">{successMessage}</p>
            ) : null}
            <div className="register-row">
              <label className="register-label" htmlFor="register-name">
                Name <span>*</span>
              </label>
              <div className="register-separator">:</div>
              <input
                id="register-name"
                name="name"
                className="register-input"
                type="text"
                autoComplete="off"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="register-row">
              <label className="register-label" htmlFor="register-phone">
                Phone <span>*</span>
              </label>
              <div className="register-separator">:</div>
              <input
                id="register-phone"
                name="phone"
                className="register-input"
                type="text"
                autoComplete="off"
                value={formData.phone}
                onChange={handleChange}
                maxLength={10}
              />
            </div>

            <div className="register-row">
              <label className="register-label" htmlFor="register-email">
                Email <span>*</span>
              </label>
              <div className="register-separator">:</div>
              <input
                id="register-email"
                name="email"
                className="register-input"
                type="email"
                autoComplete="off"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="register-row">
              <label className="register-label" htmlFor="register-password">
                Password <span>*</span>
              </label>
              <div className="register-separator">:</div>
              <input
                id="register-password"
                name="password"
                className="register-input"
                type="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <div className="register-row register-row--textarea">
              <label className="register-label" htmlFor="register-address">
                Address
              </label>
              <div className="register-separator">:</div>
              <textarea
                id="register-address"
                name="address"
                className="register-input register-textarea"
                rows="4"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            <div className="register-row">
              <label className="register-label" htmlFor="register-security">
                Security Code <span>*</span>
              </label>
              <div className="register-separator">:</div>
              <div className="security-code-row">
                <div className="security-box">{securityPrompt.left}</div>
                <span className="security-symbol">+</span>
                <div className="security-box">{securityPrompt.right}</div>
                <span className="security-symbol">=</span>
                <input
                  id="register-security"
                  name="securityAnswer"
                  className="register-input security-answer"
                  type="text"
                  value={formData.securityAnswer}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="register-actions">
              <button type="submit" className="register-btn" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
              <button type="reset" className="register-btn" onClick={handleReset} disabled={isSubmitting}>
                Reset
              </button>
            </div>
          </form>
        </section>
      </main>
    </>
  );
}
